import { useTranslation } from 'react-i18next';
import { uiLocale } from '../../shared/format';
import { problemKind } from '../../shared/problem-text';
import { useProblemText } from '../../shared/use-problem-text';
import { Btn, Note } from '../admin-kit';
import './werkvloer.css';

/**
 * Wat de werkvloerschermen van het beheer (BHR-6) delen en de kit niet heeft: de fouttoestand van
 * een lijst, laden en de korte tijd. De iconen en de filterknop (`FilterSelect`) staan sinds OPR-2
 * in de kit.
 *
 * Bewust hier en niet in `admin-kit`: acht delen werken tegelijk aan het portaal, en wat maar in
 * één deel nodig is, hoort niet in het gedeelde bestand (zie `docs/design/admin-kit.md`).
 */

/**
 * Een lijst die niet opgehaald kon worden: niet hetzelfde als een lege lijst (Extra 2, punt 14),
 * dus een rood vlak met een knop om het opnieuw te proberen.
 */
export function LoadError({ onRetry, error }: { onRetry: () => void; error?: unknown }) {
  const { t } = useTranslation();
  const text = useProblemText();
  // Een ontbrekend recht: uitleggen, en geen knop die het niet oplost (FBK-2).
  const denied = problemKind(error) === 'denied';
  return (
    <div className="a-card-pad" data-testid="list-error">
      <Note tone="danger" role="alert">
        {denied ? text(error, '') : t('errors.listFailed')}
      </Note>
      {denied ? null : (
        <div>
          <Btn variant="sec" size="sm" onClick={onRetry}>
            {t('common.retry')}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Laden in een kaart: één rustige zin in plaats van een springende lijst. */
export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="a-card-pad" role="status" aria-live="polite">
      <span className="a-muted">{t('common.loading')}</span>
    </div>
  );
}

/**
 * De korte tijd in de stijl van de mockup: "vandaag 10:00", "gisteren 14:12" of "di 08/09 · 10:00".
 * In de taal van de lezer (`uiLocale`), zoals de rest van het portaal.
 */
export function useShortWhen() {
  const { t } = useTranslation();
  return (value: string | null | undefined): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const locale = uiLocale();
    const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
      date,
    );
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(date, now)) return t('beheer.werkvloer.when.today', { time });
    if (sameDay(date, yesterday)) return t('beheer.werkvloer.when.yesterday', { time });
    const day = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date).replace('.', '');
    const dayMonth = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(
      date,
    );
    return `${day} ${dayMonth} · ${time}`;
  };
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
